import React, { useEffect, useState } from "react";
import usersService from "../../../services/UsersService";
import SwitchCom from "../../Helpers/SwitchCom";

// Same 5 toggles, same copy, same behavior as myfit-app's
// NotificationSettingsScreen — /user/notification-settings is the same
// endpoint either platform calls. Not carried over: myfit-app's OneSignal
// login/logout side effect on allow_notifications, since that's a native
// push-device binding with no web equivalent here.
const TOGGLES = [
  {
    key: "allow_notifications",
    label: "Allow Notification",
    description: "Turn all notifications on or off, including the ones below.",
  },
  {
    key: "workout_reminders",
    label: "Workout reminders",
    description: "A nudge when it’s time for your scheduled workout.",
  },
  {
    key: "weekly_summary",
    label: "Weekly summary",
    description: "A recap of your progress every Monday.",
  },
  {
    key: "product_updates",
    label: "Product updates",
    description: "News about new features and improvements.",
  },
  {
    key: "notify_by_email",
    label: "Notify by email",
    description: "Also send these notifications to your email address.",
  },
];

const DEFAULTS = {
  allow_notifications: true,
  workout_reminders: true,
  weekly_summary: true,
  product_updates: false,
  notify_by_email: false,
};

export default function NotificationSettingTab() {
  const api = new usersService();

  const [values, setValues] = useState(DEFAULTS);
  const [isLoading, setIsLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .fetchNotificationSettings()
      .then((res) => setValues(res.data))
      .catch(() => setError("Could not load settings."))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async (key) => {
    const previous = values;
    const next = { ...values, [key]: !values[key] };
    setValues(next);
    setSavingKey(key);
    setError(null);
    try {
      await api.updateNotificationSettings({ [key]: next[key] });
    } catch (err) {
      setValues(previous); // revert on failure
      setError("Could not save. Try again.");
    } finally {
      setSavingKey(null);
    }
  };

  if (isLoading) {
    return (
      <div className="notification-setting-tab w-full">
        <div className="lg:flex justify-center">
          <div style={{ transform: "translateY(150px)" }} role="status">
            <svg
              aria-hidden="true"
              className="w-32 h-32 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="notification-setting-tab w-full">
      {error && <p className="text-red-700 mb-4">{error}</p>}
      <div className="notification-settings-wrapper w-full">
        <ul className="notification-settings-items w-full">
          {TOGGLES.map((item, i) => (
            <li
              key={item.key}
              className={`notification-settings-item sm:flex justify-between items-center ${
                i === TOGGLES.length - 1 ? "" : "mb-4 pb-4 border-b"
              } dark:border-[#5356fb29] border-light-purple dark:border-[#5356fb29]`}
            >
              <div>
                <p className="text-xl tracking-wide text-dark-gray dark:text-white font-bold">
                  {item.label}
                </p>
                <p className="text-base tracking-wide text-thin-light-gray">
                  {item.description}
                </p>
              </div>
              <div className="mt-5 sm:mt-0">
                {savingKey === item.key ? (
                  <span className="text-sm text-thin-light-gray">Saving…</span>
                ) : (
                  <SwitchCom value={values[item.key]} handler={() => toggle(item.key)} />
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
